import React, { useEffect } from 'react';
import { generatePath, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Card, CardActions, CardContent, useMediaQuery, useTheme } from '@mui/material';
import moment from 'moment';
import * as authService from '../services/auth';
import * as uiPersistedActions from '../actions/uiPersisted';
import { fetchRescuerPastRescuesIfNeeded } from '../actions/rescues';
import useActiveUser from '../hooks/useActiveUser';
import { getRecentRescues } from '../helpers/RescuesHelper';
import routes from '../routes';

const CloseRescueReminderNotification = () => {
  const user = authService.getCurrentlyLoggedInOrImpersonatingUser();

  const history = useHistory();
  const dispatch = useDispatch();
  const activeUser = useActiveUser();
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.only('xs'), {
    defaultMatches: null,
  });
  const isDrawerOpen = useSelector((state) => state.ui.persisted.isDrawerOpen);
  const closeRescueReminderNotificationLastDismissDate = useSelector(
    (state) => state.ui.persisted.closeRescueReminderNotificationLastDismissDate
  );
  const rescuesEntities = useSelector((state) => state.entities.rescues);
  const recentRescues = getRecentRescues(activeUser.id, rescuesEntities);
  const unresolvedRecentRescues = recentRescues.filter(
    (rescue) => rescue.completed === null && rescue.cancelled_by_id === null
  );

  const paddingLeftValueAboveSmBreakpoint = (() => {
    if (user && !authService.shouldHideMainDrawer(user)) {
      return isDrawerOpen ? '240px' : '70px';
    }

    return '16px';
  })();

  useEffect(() => {
    dispatch(fetchRescuerPastRescuesIfNeeded(activeUser.id, false, 15)); // note: fetching only last 15 past rescues
  }, []);

  if (!unresolvedRecentRescues.length) {
    return null;
  }

  if (
    closeRescueReminderNotificationLastDismissDate &&
    moment(closeRescueReminderNotificationLastDismissDate).isAfter(unresolvedRecentRescues[0].date)
  ) {
    return null;
  }

  const onDismiss = () => {
    dispatch(uiPersistedActions.dismissCloseRescueReminderNotification());
  };

  const onRescueClose = () => {
    dispatch(uiPersistedActions.dismissCloseRescueReminderNotification());

    if (unresolvedRecentRescues.length > 1) {
      if (!isMobileView) {
        history.push(generatePath(routes.dashboardRescuer));
        return setTimeout(() => {
          const yOffset = -120;
          const element = document.getElementById('recent-rescues');
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }, 500);
      }

      return history.push(generatePath(routes.mobileDashboardMyRescues, { tab: 'recent-rescues' }));
    }

    return history.push(generatePath(routes.rescueClose, { rescueId: unresolvedRecentRescues[0].id }));
  };

  return (
    <Card
      sx={{
        backgroundColor: '#e1ebfd',
        width: '100%',
        paddingLeft: {
          xs: '8px',
          sm: paddingLeftValueAboveSmBreakpoint,
        },
      }}
    >
      <CardContent style={{ paddingBottom: 0 }} data-testid="close-rescue-reminder-card">
        <Box sx={{ fontSize: '16px', fontWeight: 'bold' }}>
          You have unclosed {unresolvedRecentRescues.length > 1 ? 'rescues' : 'rescue'}
        </Box>

        <Box
          sx={{
            fontSize: '13px',
            color: 'rgba(0, 0, 0, 0.54)',
          }}
        >
          {unresolvedRecentRescues.length === 1
            ? 'Click Close Rescue button to close it.'
            : 'Go to Recent Rescues to close all your unclosed rescues.'}
        </Box>
      </CardContent>

      <CardActions>
        <Button
          size="small"
          color="primary"
          variant="contained"
          onClick={onRescueClose}
          data-testid="resend-email-button"
        >
          {unresolvedRecentRescues.length > 1 ? 'Recent Rescues' : 'Close Rescue'}
        </Button>

        <Button size="small" color="primary" onClick={onDismiss}>
          Do this later
        </Button>
      </CardActions>
    </Card>
  );
};

export default CloseRescueReminderNotification;
